import React from "react";
import { Settings as SettingsIcon } from "lucide-react";
import { ROUTE_KEYS, ROUTE_META, useAISettings } from "@/components/ai/aiRoutes";
import RouteConfigForm from "@/components/ai/RouteConfigForm";
import ProviderKeysPanel from "@/components/ai/ProviderCredentialsPanel";
import AIHealthPanel from "@/components/ai/AIHealthPanel";
import PromptTemplates from "@/components/ai/PromptTemplates";

function Section({ title, subtitle, children }) {
  return (
    <section className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
      <header className="mb-4">
        <h2 className="text-base font-semibold text-zinc-100">{title}</h2>
        {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

export default function AISettings() {
  const { settings, loading, error, reload } = useAISettings();

  return (
    <div className="p-4 md:p-6 space-y-6 text-zinc-100">
      <div className="flex items-center gap-3">
        <SettingsIcon className="text-purple-400" size={22} />
        <div>
          <h1 className="text-xl font-semibold">AI Settings</h1>
          <p className="text-xs text-zinc-500">
            Configure providers, models, and fallback order for each AI route.
          </p>
        </div>
      </div>

      {error && (
        <div className="border border-red-700/50 bg-red-900/20 text-red-300 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}
      {loading && !settings && <div className="text-sm text-zinc-500">Loading AI settings…</div>}

      {settings && (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {ROUTE_KEYS.map((route) => (
              <Section
                key={route}
                title={ROUTE_META[route].title}
                subtitle={`Used for: ${ROUTE_META[route].platforms}`}
              >
                <RouteConfigForm route={route} settings={settings} onSaved={reload} />
              </Section>
            ))}
          </div>

          <Section
            title="Providers"
            subtitle="Provider credentials are server-side only and are never returned in full."
          >
            <ProviderKeysPanel settings={settings} onSaved={reload} />
          </Section>

          <Section title="Health and usage" subtitle="Provider health, recent requests, token use, and estimated costs.">
            <AIHealthPanel settings={settings} />
          </Section>

          <Section title="Prompt templates" subtitle="Built-in templates are read-only. Each template runs only in its own mode.">
            <PromptTemplates />
          </Section>
        </>
      )}
    </div>
  );
}
